import { AfterViewInit, Component, effect, inject, OnDestroy, ViewChild, ViewContainerRef, ChangeDetectionStrategy } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { MsalRedirectComponent } from '@azure/msal-angular';
import { Subscription } from 'rxjs';

import { AuthService } from './shared/auth/auth.service';
import { ApplicationInsightsService } from './shared/services/application-insights.service';
import { SessionStore } from './shared/state/session.store';
import { LoaderService } from './shared/services/loader.service';
import { MAIN_LOADER_ID, PLP_SCROLL_PROP } from './shop/shared/constants/shared.constants';
import { UtilityFunctions } from './shared/utilities/functions.models';
import { environment } from 'environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { CoreTranslationService } from './shared/services/translation.service';
import { CultureList } from './shared/culture/culture.model';
import { ItTranslation } from './shared/culture/traslations/it';
import { EnTranslation } from './shared/culture/traslations/en';
import { EsTranslation } from './shared/culture/traslations/es';
import { FrTranslation } from './shared/culture/traslations/fr';
import { DeTranslation } from './shared/culture/traslations/de';
import { WorkInProgressComponent } from './back-office/shared/components/errors/work-in-progress/work-in-progress.component';
import { LoaderModule } from './shared/components/loader/loader.module';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    imports: [RouterOutlet, LoaderModule, WorkInProgressComponent],
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./app.component.scss']
})
export class AppComponent implements AfterViewInit, OnDestroy {

  @ViewChild('msalRedirect', { read: ViewContainerRef }) msalContainer: ViewContainerRef;

  public loaderId = MAIN_LOADER_ID;
  public initialized = false;
  public block = false;
  public workInProgressTitle = "";
  private subscription = new Subscription();

  private _session = inject(SessionStore);

  constructor(
    private _loaderService: LoaderService,
    private _router: Router,
    private _auth: AuthService,
    private _translationService: TranslateService,
    private _coreTranslationService: CoreTranslationService,
    private ApplicationInsightsService: ApplicationInsightsService
  ) {
    this.checkAuthSubscribe();
    this.langInitAndSetBlock();
    this.syncDocumentLang();

    this._loaderService.showLoader(this.loaderId);
    this._router.events.subscribe(val => {
      if (val instanceof NavigationStart && val.url.indexOf(PLP_SCROLL_PROP) < 0)
        this._loaderService.showLoader(this.loaderId);
      else if (val instanceof NavigationCancel || val instanceof NavigationEnd)
        this._loaderService.dismissLoader(this.loaderId);
    });
  }

  ngAfterViewInit(): void {
    this.msalContainer.createComponent(MsalRedirectComponent);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  private langInitAndSetBlock() {
    const culture = new CultureList();
    // Era un subscribe su userInfoChanged$: l'utente arriva dopo l'avvio, serve reagire al cambio.
    effect(() => {
      const data = this._session.userInfo();
      if (data) {
        const block = environment.blockedTenants?.some(x => x == UtilityFunctions.getEnvironmentTenant(data.tenant?.id)?.tenantType) && !UtilityFunctions.IsAdminOrStoreOwner(this._session.userInfo());
        if (block) {
          this._translationService.addLangs(culture.getShortTitleList());
          this._coreTranslationService.translate(ItTranslation, EnTranslation, EsTranslation, FrTranslation, DeTranslation);
          const fromHour = '18:00';
          const fromDate = '26/11/2025';
          const toHour = '10:00';
          const toDate = '08/12/2025';
          this._translationService.setDefaultLang(culture.getShortTitle(data.culture));
          this.workInProgressTitle = this._translationService.instant('WORK_IN_PROGRESS_TITLE', { fromHour, fromDate, toHour, toDate })
          this.block = block;
        }
      }
    });
  }

    private syncDocumentLang() {
    const lang =
      this._translationService.currentLang ||
      this._translationService.defaultLang;

    if (lang) {
      document.documentElement.lang = lang;
    }

    this.subscription.add(
      this._translationService.onLangChange.subscribe(e => {
        if (e.lang) {
          document.documentElement.lang = e.lang;
        }
      })
    );
  }

  private checkAuthSubscribe() {
    effect(() => {
      const data = this._session.userInfo();
      if (data) this.ApplicationInsightsService.setUserId(data.identityId);
    });
    this.subscription.add(this._auth.isLogged.subscribe(data => this.initialized = data));
  }
}
